import { supabase } from '../lib/supabase';
import {
  readImpersonationState,
  setImpersonationState,
  popDeveloperSession,
} from '../lib/session';
import './ImpersonationBanner.css';

/**
 * Visible when a developer is impersonating a user. Loads dev's stashed
 * session token back onto the client to exit. Endpoint is /api/admin/impersonate/end.
 */

export function ImpersonationBanner() {
  const state = readImpersonationState();
  if (!state.active) return null;

  async function exit() {
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (state.session_id && token) {
        await fetch('/api/admin/impersonate/end', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ session_id: state.session_id }),
        }).catch(() => {});
      }
    } finally {
      // Clear the impersonation flag FIRST so we can never end up stuck showing
      // the banner if a later step throws.
      setImpersonationState({ active: false, session_id: null, target_display_name: null });
      // Restore the developer's original session if we still have it stashed.
      const dev = popDeveloperSession();
      if (dev) {
        await supabase.auth.setSession({
          access_token: dev.access_token,
          refresh_token: dev.refresh_token,
        });
        window.location.assign('/admin');
      } else {
        // The stash is sessionStorage-scoped, so opening the impersonation in a
        // new tab (or after the tab was closed) loses it. Don't silently drop
        // the operator on /admin signed-in as the target - sign out fully and
        // send them to a clean re-auth.
        await supabase.auth.signOut();
        window.location.assign('/sign-in?ended=impersonation');
      }
    }
  }

  return (
    <div className="imp-banner" role="status" aria-live="polite">
      <span className="imp-banner__text">
        Viewing as <strong>{state.target_display_name ?? 'user'}</strong>.
      </span>
      <button type="button" className="imp-banner__exit" onClick={exit}>Exit →</button>
    </div>
  );
}
